import { Component, Input, OnInit, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { lastValueFrom } from 'rxjs';
import {
  Advertiser,
  AdvertiserLoginOptions,
  CampaignsCampaignAdminApiClient,
} from 'src/app/api/api-campaigns.service.generated';
import { ApplicationUserSettingsUMClient } from 'src/app/api/api-user-management.service.generated';
import { LoadingAwareComponent } from 'src/app/common/utils/loading-aware.component';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { ButtonComponent } from 'wecheer-common';
import {
  AdvertiserLoginOptionsConfirmDialogComponent,
  LoginOptionsConfirmData,
} from './advertiser-login-options-confirm-dialog.component';


@Component({
  selector: 'app-advertiser-login-options',
  templateUrl: './advertiser-login-options.component.html',
  styleUrls: ['./advertiser-login-options.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatSlideToggleModule,
    MatSelectModule,
    MatOptionModule,
    MatProgressBar,
    MatProgressSpinner,
    MatInputModule,
    MatFormFieldModule,
    MatSnackBarModule,
    ButtonComponent
],
})
export class AdvertiserLoginOptionsComponent extends LoadingAwareComponent implements OnInit {
  private fb = inject(FormBuilder);
  private campaignsClient = inject(CampaignsCampaignAdminApiClient);
  private appSettingsClient = inject(ApplicationUserSettingsUMClient);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private destroyRef = inject(DestroyRef);

  isSaving = false;

  _advertiser: Advertiser;
  get advertiser() {
    return this._advertiser;
  }
  @Input() set advertiser(value: Advertiser) {
    this._advertiser = value;
    this.refresh();
  }

  form: FormGroup = this.fb.group({
    phoneOtpEnabled: [false],
    emailOtpEnabled: [false],
    emailPasswordEnabled: [false],
    googleEnabled: [false],
    appleEnabled: [false],
    batSsoEnabled: [false],
    defaultLoginMethod: ['Phone'],
    allowedCountryCodes: [''],
  });

  defaultLoginMethodOptions = [
    { value: 'Phone', label: 'Phone' },
    { value: 'Email', label: 'Email' },
  ];

  get showDefaultLoginMethod(): boolean {
    return this.form.get('phoneOtpEnabled')!.value || this.form.get('emailOtpEnabled')!.value;
  }

  get availableLoginMethodOptions() {
    const phoneOn = this.form.get('phoneOtpEnabled')!.value;
    const emailOn = this.form.get('emailOtpEnabled')!.value;
    return this.defaultLoginMethodOptions.filter(o =>
      (o.value === 'Phone' && phoneOn) || (o.value === 'Email' && emailOn)
    );
  }

  ngOnInit() {
    this.form.get('phoneOtpEnabled')!.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(enabled => {
        if (enabled) {
          this.form.patchValue({ defaultLoginMethod: 'Phone' }, { emitEvent: false });
        } else if (this.form.get('defaultLoginMethod')!.value === 'Phone') {
          this.form.patchValue({ defaultLoginMethod: this.form.get('emailOtpEnabled')!.value ? 'Email' : null }, { emitEvent: false });
        }
      });

    this.form.get('emailOtpEnabled')!.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(enabled => {
        if (enabled && !this.form.get('phoneOtpEnabled')!.value) {
          this.form.patchValue({ defaultLoginMethod: 'Email' }, { emitEvent: false });
        } else if (!enabled && this.form.get('defaultLoginMethod')!.value === 'Email') {
          this.form.patchValue({ defaultLoginMethod: this.form.get('phoneOtpEnabled')!.value ? 'Phone' : null }, { emitEvent: false });
        }
      });
  }

  async refresh() {
    if (!this._advertiser) return;

    this.isLoading = true;
    try {
      const options = await lastValueFrom(
        this.campaignsClient.getAdvertiserLoginOptions(this._advertiser.id)
      );

      if (options) {
        this.form.patchValue({
          phoneOtpEnabled: options.phoneOtpEnabled,
          emailOtpEnabled: options.emailOtpEnabled,
          emailPasswordEnabled: options.emailPasswordEnabled,
          googleEnabled: options.googleEnabled,
          appleEnabled: options.appleEnabled,
          batSsoEnabled: options.batSsoEnabled,
          defaultLoginMethod: options.defaultLoginMethod ?? 'Phone',
          allowedCountryCodes: options.allowedCountryCodes?.join(', ') ?? '',
        });
      }
    } catch (error) {
      console.error('Error loading login options:', error);
    } finally {
      this.isLoading = false;
    }
  }

  async onSave() {
    if (!this.form.valid) return;

      const rawValue = this.form.value;
      const allowedCountryCodes = rawValue.allowedCountryCodes
        ? (rawValue.allowedCountryCodes as string)
            .split(',')
            .map((c: string) => c.trim().toUpperCase())
            .filter((c: string) => c.length > 0)
        : [];

    const allMethodsOff =
      !rawValue.phoneOtpEnabled &&
      !rawValue.emailOtpEnabled &&
      !rawValue.emailPasswordEnabled &&
      !rawValue.googleEnabled &&
      !rawValue.appleEnabled &&
      !rawValue.batSsoEnabled;

    let dialogConfig: LoginOptionsConfirmData['config'] = {
        ...rawValue,
      allowedCountryCodes: rawValue.allowedCountryCodes ?? '',
    };
    let isUsingDefault = false;

    if (allMethodsOff) {
      try {
        const setting = await lastValueFrom(
          this.appSettingsClient.getByName2('LoginOptionsDefault')
        );
        if (setting?.value) {
          const defaultConfig = JSON.parse(setting.value);
          dialogConfig = {
            phoneOtpEnabled: defaultConfig.phoneOtpEnabled ?? false,
            emailOtpEnabled: defaultConfig.emailOtpEnabled ?? false,
            emailPasswordEnabled: defaultConfig.emailPasswordEnabled ?? false,
            googleEnabled: defaultConfig.googleEnabled ?? false,
            appleEnabled: defaultConfig.appleEnabled ?? false,
            batSsoEnabled: defaultConfig.batSsoEnabled ?? false,
            defaultLoginMethod: defaultConfig.defaultLoginMethod ?? null,
            allowedCountryCodes: rawValue.allowedCountryCodes ?? '',
          };
          isUsingDefault = true;
        }
      } catch {
        // fetch failed — show form values (all off) as-is
      }
    }

    const confirmed = await lastValueFrom(
      this.dialog.open(AdvertiserLoginOptionsConfirmDialogComponent, {
        width: '480px',
        data: { config: dialogConfig, isUsingDefault } satisfies LoginOptionsConfirmData,
      }).afterClosed()
    );

    if (!confirmed) return;

    const payload = AdvertiserLoginOptions.fromJS({ ...rawValue, allowedCountryCodes });
      this.isSaving = true;
      try {
        await lastValueFrom(
          this.campaignsClient.updateAdvertiserLoginOptions(this._advertiser.id, payload)
        );
        this.snackBar.open('Login options saved successfully', 'Close', { duration: 2000 });
      } catch (error) {
        console.error('Error saving login options:', error);
        this.snackBar.open('Failed to save login options', 'Close', { duration: 2000 });
      } finally {
        this.isSaving = false;
      }
  }
}
